import { ReportItemColumnTypeEnum } from '@zaoblako/shared/common/enums/report-item-column-type.enum';
import type { CardFieldDescriptorInterface } from '@zaoblako/shared/common/dtos/card/card-field-descriptor.dto';

import { isEqual, isNil } from 'lodash';

export interface CardFieldValueEqualContext {
  fieldKey?: string;
  field?: CardFieldDescriptorInterface;
}

const normalizeEmpty = (value: unknown): unknown => {
  if (value === '' || isNil(value)) {
    return null;
  }
  return value;
};

const normalizeDateValue = (value: unknown): unknown => {
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === 'string') {
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? value : time;
  }
  return value;
};

const normalizeFrmEntityValue = (value: unknown): unknown => {
  if (isNil(value) || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const record = value as Record<string, unknown>;
  if (!isNil(record['id'])) {
    return { id: record['id'] };
  }
  return value;
};

const shouldNormalizeFrmEntity = (
  fieldKey: string | undefined,
  field: CardFieldDescriptorInterface | undefined
): boolean =>
  field?.type === ReportItemColumnTypeEnum.FRM_ENTITY
  || Boolean(field?.frmEntityType)
  || fieldKey === 'organizationBankDetails'
  || fieldKey === 'contragentBankDetails';

const shouldNormalizeDate = (fieldKey: string | undefined, field: CardFieldDescriptorInterface | undefined): boolean =>
  field?.type === ReportItemColumnTypeEnum.DATE
  || field?.isDateTime === true
  || fieldKey === 'date'
  || fieldKey === 'deadline';

export const isCardFieldValueEqual = (
  previousValue: unknown,
  nextValue: unknown,
  context?: CardFieldValueEqualContext
): boolean => {
  const fieldKey = context?.fieldKey ?? context?.field?.key;
  const field = context?.field;

  let left = normalizeEmpty(previousValue);
  let right = normalizeEmpty(nextValue);

  if (shouldNormalizeDate(fieldKey, field)) {
    left = normalizeDateValue(left);
    right = normalizeDateValue(right);
  }

  if (shouldNormalizeFrmEntity(fieldKey, field)) {
    left = normalizeFrmEntityValue(left);
    right = normalizeFrmEntityValue(right);
  }

  return isEqual(left, right);
};
